import { apiFetch } from './client';

export type GatewayKind = 'stripe' | 'usaepay' | 'cardknox';

export interface PaymentGatewayRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  ref_number: string;
  name: string;
  kind: GatewayKind;
  is_account_default: boolean;
  active: boolean;
  has_credentials: boolean;
  created_at: string;
  updated_at: string;
}

export type GatewayCredentialsBody =
  | { kind: 'stripe'; secret_key: string; publishable_key?: string }
  | { kind: 'usaepay'; api_key: string; api_pin: string }
  | { kind: 'cardknox'; api_key: string };

export interface CreateGatewayBody {
  name: string;
  kind: GatewayKind;
  customer_id: string;
  credentials: GatewayCredentialsBody;
  is_account_default?: boolean;
}

export type PatchGatewayBody = Partial<{
  name: string;
  credentials: GatewayCredentialsBody;
  is_account_default: boolean;
  active: boolean;
}>;

export const paymentGateways = {
  list: (p: { customer_id?: string; kind?: GatewayKind; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (p.customer_id) q.set('customer_id', p.customer_id);
    if (p.kind) q.set('kind', p.kind);
    if (p.limit) q.set('limit', String(p.limit));
    return apiFetch<{ rows: PaymentGatewayRow[] }>(`/api/payment-gateways?${q}`);
  },
  get: (id: string) => apiFetch<PaymentGatewayRow>(`/api/payment-gateways/${id}`),
  create: (body: CreateGatewayBody) =>
    apiFetch<PaymentGatewayRow>('/api/payment-gateways', {
      method: 'POST', body: JSON.stringify(body),
    }),
  update: (id: string, patch: PatchGatewayBody) =>
    apiFetch<PaymentGatewayRow>(`/api/payment-gateways/${id}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }),
  remove: (id: string) =>
    apiFetch<void>(`/api/payment-gateways/${id}`, { method: 'DELETE' }),
  /**
   * Verify the saved credentials with the gateway via an auth-only round
   * trip. ok=false means the gateway rejected the keys; the message is the
   * provider's own response (Stripe permission_error, USAePay 401 body,
   * Cardknox 'Invalid Key', etc.).
   */
  verify: (id: string) =>
    apiFetch<{ ok: boolean; message: string }>(
      `/api/payment-gateways/${id}/verify`,
      { method: 'POST', body: '{}' },
    ),
};
